import React, { useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  AlertCircle,
  Ban,
  CheckCircle,
  Clock,
  PauseCircle,
  Shield,
  XCircle,
} from 'lucide-react';
import { buildCertificateLifecycleHealthStats, isLifecycleTileActive } from '../../utils/certificateLifecycleUtils';
import DashboardViewAllButton from './DashboardViewAllButton';

const HEALTH_SLICE_COLORS = { Active: '#22c55e', 'Expiring Soon': '#f59e0b', Expired: '#ef4444' };

const healthIconMap = {
  'Total Certificates': Shield,
  Active: CheckCircle,
  'Expiring Soon': Clock,
  Expired: AlertCircle,
};

const lifecycleIconMap = {
  Active: CheckCircle,
  Valid: CheckCircle,
  Suspended: PauseCircle,
  Withdrawn: XCircle,
  Terminate: XCircle,
  'Discontinue Not Active': Ban,
  'Discontinue Active': Ban,
};

const MetricTile = ({ label, value, color, bg, bgIcon, icon: Icon, onClick, active, clickable }) => (
  <div
    role={clickable ? 'button' : undefined}
    tabIndex={clickable ? 0 : undefined}
    onClick={onClick}
    onKeyDown={clickable ? (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick?.();
      }
    } : undefined}
    className="d-flex align-items-center gap-3 p-3 rounded-3 h-100"
    title={clickable ? `Show only ${label} certificates` : undefined}
    style={{
      backgroundColor: bg,
      cursor: clickable ? 'pointer' : 'default',
      border: active ? `2px solid ${color}` : '2px solid transparent',
      boxShadow: active ? `0 0 0 3px ${color}33` : 'none',
      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: bgIcon,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={20} color={color} />
    </div>
    <div className="min-w-0">
      <div className="fw-bold" style={{ fontSize: 24, color, lineHeight: 1 }}>{value ?? 0}</div>
      <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
    </div>
  </div>
);

const CertificateLifecycleHealthCard = ({
  stats,
  certificates = [],
  title = 'Certificate Health & Lifecycle Status',
  className = 'mb-4',
  loading = false,
  onViewAll,
  activeStandard = '',
  activeLifecycleStatus = '',
  onLifecycleTileClick,
}) => {
  const { health, lifecycleIso, lifecycleIscc } = buildCertificateLifecycleHealthStats(stats, certificates);
  const [selectedHealthSlice, setSelectedHealthSlice] = useState(null);
  const healthSlices = [
    { label: 'Active', value: health.active || 0 },
    { label: 'Expiring Soon', value: health.expiringSoon || 0 },
    { label: 'Expired', value: health.expired || 0 },
  ].filter((slice) => slice.value > 0);
  const hasIsoLifecycleData = lifecycleIso.some((item) => item.count > 0);
  const hasIsccLifecycleData = lifecycleIscc.some((item) => item.count > 0);

  const healthTiles = [
    { label: 'Total Certificates', value: health.total, color: '#0ea5e9', bg: '#f0f9ff', bgIcon: '#e0f2fe' },
    { label: 'Active', value: health.active, color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7' },
    { label: 'Expiring Soon', value: health.expiringSoon, color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7' },
    { label: 'Expired', value: health.expired, color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
  ];

  return (
    <div className={`card border-0 shadow-sm ${className}`} style={{ borderRadius: 12 }}>
      <div className="card-body p-4">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="#f59e0b" />
          </div>
          <div className="me-auto">
            <h5 className="mb-0 fw-semibold" style={{ color: '#1e293b' }}>{title}</h5>
            <div className="text-muted small">Expiry health and AuditQ lifecycle status in one view.</div>
          </div>
          {onViewAll && <DashboardViewAllButton onClick={onViewAll} />}
        </div>

        {loading ? (
          <div className="row g-3">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="col-6 col-md-3">
                <div className="placeholder-glow">
                  <span className="placeholder col-12 rounded-3" style={{ height: 72 }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mb-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              {healthTiles.map((tile) => (
                <MetricTile key={tile.label} {...tile} icon={healthIconMap[tile.label]} />
              ))}
            </div>

            {healthSlices.length > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Health Overview</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{health.total} total</span>
                </div>
                <div className="row g-2 align-items-center">
                  <div className="col-12 col-sm-7">
                    <ReactApexChart
                      type="donut"
                      height={180}
                      series={healthSlices.map((slice) => slice.value)}
                      options={{
                        chart: {
                          type: 'donut',
                          events: {
                            dataPointSelection: (_event, _context, config) => {
                              const slice = healthSlices[config.dataPointIndex];
                              setSelectedHealthSlice((current) => (current === slice?.label ? null : slice?.label));
                            },
                          },
                        },
                        labels: healthSlices.map((slice) => slice.label),
                        colors: healthSlices.map((slice) => (
                          !selectedHealthSlice || selectedHealthSlice === slice.label
                            ? HEALTH_SLICE_COLORS[slice.label]
                            : `${HEALTH_SLICE_COLORS[slice.label]}55`
                        )),
                        stroke: { width: 2, colors: ['#fff'] },
                        dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 700 } },
                        legend: { show: false },
                        plotOptions: { pie: { donut: { size: '62%', labels: { show: true, total: { show: true, label: 'Total', fontSize: '11px' } } } } },
                        tooltip: { y: { formatter: (value) => `${value} certificate${value === 1 ? '' : 's'}` } },
                      }}
                    />
                  </div>
                  <div className="col-12 col-sm-5">
                    <div className="d-flex flex-column gap-2">
                      {healthSlices.map((slice) => (
                        <button
                          type="button"
                          key={slice.label}
                          onClick={() => setSelectedHealthSlice((current) => (current === slice.label ? null : slice.label))}
                          className="d-flex align-items-center gap-2 border-0 bg-transparent p-0 text-start"
                          style={{ cursor: 'pointer', opacity: !selectedHealthSlice || selectedHealthSlice === slice.label ? 1 : 0.45, transitionProperty: 'opacity', transitionDuration: '150ms' }}
                          aria-pressed={selectedHealthSlice === slice.label}
                        >
                          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: HEALTH_SLICE_COLORS[slice.label], flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>{slice.value}</span>
                          <span style={{ fontSize: 11, color: '#64748b' }}>{slice.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasIsoLifecycleData && (
              <div className="mb-3">
                <div className="fw-semibold mb-2" style={{ fontSize: 12, color: '#475569' }}>
                  Certificate ISO Status
                </div>
                {/* CSS grid, not a Bootstrap flex row: a flex column (col-6 col-lg) can shrink
                    below its content's natural width when the parent card sits in a narrow
                    column (e.g. half-width on /audit-dashboard) — with 5-6 tiles that shrank to
                    ~50px each, forcing labels like "Discontinue Not Active" to wrap one character
                    per line. minmax(150px, 1fr) guarantees a floor: tiles wrap to fewer per row
                    instead of ever going narrower than they can legibly render. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                  {lifecycleIso.map((item) => (
                    <MetricTile
                      key={item.value}
                      {...item}
                      value={item.count}
                      icon={lifecycleIconMap[item.label] || Shield}
                      clickable={Boolean(onLifecycleTileClick)}
                      active={isLifecycleTileActive('ISO', item.label, activeStandard, activeLifecycleStatus)}
                      onClick={() => onLifecycleTileClick?.('ISO', item.label)}
                    />
                  ))}
                </div>
              </div>
            )}

            {hasIsccLifecycleData && (
              <div>
                <div className="fw-semibold mb-2" style={{ fontSize: 12, color: '#475569' }}>
                  Certificate ISCC Status
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                  {lifecycleIscc.map((item) => (
                    <MetricTile
                      key={item.value}
                      {...item}
                      value={item.count}
                      icon={lifecycleIconMap[item.label] || Shield}
                      clickable={Boolean(onLifecycleTileClick)}
                      active={isLifecycleTileActive('ISCC', item.label, activeStandard, activeLifecycleStatus)}
                      onClick={() => onLifecycleTileClick?.('ISCC', item.label)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CertificateLifecycleHealthCard;
